import store from '@/store'
import { isLocalId } from '@/utils/Id'
import { returnTo, sameQuery } from '@/router/navigation'

/**
 * The map screen keeps its state in the `map` store module and mirrors it into the address:
 * /map/:floorId?pointId=&print=&edit=. Both directions go through here, so a change on one
 * side never comes back from the other as a second navigation.
 */

/** The map state an address describes - what back / forward / a link should put on screen. */
export const mapStateFromRoute = (route) => ({
  floorId: route.params.floorId,
  activePointId: route.query.pointId ?? null,
  printMode: Boolean(route.query.print),
  editMode: Boolean(route.query.edit)
})

const same = (a, b) => String(a ?? '') === String(b ?? '')

/**
 * How the address should follow a change of the map state:
 * - null      it already says it (the change came from the address, or changed nothing)
 * - 'push'    another floor, or a desk opened: a place the back button should return to
 * - 'return'  the desk panel closed: back to the bare floor
 * - 'replace' only a mode changed, or an unsaved point got its id - not a new place
 */
export const mapHistoryMethod = (route, { params, query }) => {
  if (same(route.params.floorId, params.floorId) && sameQuery(route.query, query)) return null
  if (!same(route.params.floorId, params.floorId)) return 'push'

  const from = route.query.pointId
  const to = query.pointId
  if (same(from, to)) return 'replace'
  if (!to) return 'return'
  if (isLocalId(from)) return 'replace'
  return 'push'
}

/** Write the map state into the address, the way mapHistoryMethod says. */
export const syncMapLocation = (router, target) => {
  const location = { name: 'map', ...target }
  switch (mapHistoryMethod(router.currentRoute, target)) {
    case 'push':
      return router.push(location)
    case 'replace':
      return router.replace(location)
    case 'return':
      return returnTo(router, location)
    default:
      return Promise.resolve(router.currentRoute)
  }
}

/**
 * The two directions, for the map screen. It supplies `routeParams` (the address its state
 * describes, as { params, query }) and the `mapUpdate_` mutation.
 *
 * The screen follows the address when it changes under it - back, forward, a search result on
 * another floor - and the address follows the screen. Each side leaves the other be when it
 * already says the same, so neither answers the other with a second navigation.
 */
export const mapRouteSync = {
  watch: {
    $route(to) {
      if (to.name === 'map') this.mapUpdate_(mapStateFromRoute(to))
    },
    routeParams(target) {
      syncMapLocation(this.$router, target)
    }
  }
}

/**
 * Run `resolve` once the floors have loaded - the map guards cannot tell a floor from a
 * missing one before that. The watcher goes away after the first answer: a leftover one would
 * call a spent guard's `next` again on the next reload of the floors.
 */
export const whenFloorsLoaded = (resolve) => {
  if (store.state.floor.floorsLoaded) return resolve()
  const loaded = (state) => state.floor.floorsLoaded
  const unwatch = store.watch(loaded, (isLoaded) => {
    if (!isLoaded) return
    unwatch()
    resolve()
  })
}
